import React, { useState, useContext } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { Box, Button, FormControl, VStack, Text, useToast, IconButton } from '@chakra-ui/react';
import { FiArrowLeft } from 'react-icons/fi';
import { verifyPhoneCode } from '../../services/firebaseService';
import ReactCodeInput from 'react-code-input';
import { VerificationContext } from '../../contexts/PhoneVerificationContext';
import { DisplayHeading } from '../../components/ui';
import { ff } from '../../theme';

// react-code-input renders its own inputs; match the themed field look (fits 6 across at 375px).
const codeInputStyle: React.CSSProperties = {
  width: 46,
  height: 56,
  margin: 4,
  borderRadius: 14,
  border: `1px solid ${ff.border}`,
  fontSize: 22,
  fontWeight: 600,
  textAlign: 'center',
  color: ff.ink,
  outlineColor: ff.ink,
  background: 'white',
};

const EnterVerificationCode: React.FC = () => {
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { confirmationResult } = useContext(VerificationContext);

  const postSignInPageInfo = useLocation().state;

  const handleVerificationCodeSubmit = async () => {
    setIsLoading(true);
    try {
      const isValid = await verifyPhoneCode(confirmationResult, verificationCode);
      if (isValid) {
        navigate(postSignInPageInfo?.returnTo || '/collabs',
          postSignInPageInfo ? {
            state: postSignInPageInfo.state
          } : undefined
        );
      } else {
        toast({
          title: "Error",
          description: "Invalid verification code. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error verifying phone code:', error);
      toast({
        title: "Error",
        description: "An error occurred during phone code verification. Please try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      minH="100svh"
      display="flex"
      flexDirection="column"
      alignItems="center"
      bg="white"
      px={{ base: '20px', md: 6 }}
      pt={{ base: 4, md: 8 }}
      pb={{ base: 6, md: 10 }}
    >
      <Box width="100%" maxW="440px">
        <IconButton
          aria-label="Back"
          icon={<FiArrowLeft size={20} />}
          onClick={() => navigate(-1)}
          variant="ghost"
          color={ff.ink}
          minW="44px"
          h="44px"
          ml="-10px"
        />
      </Box>
      <VStack spacing={8} align="stretch" width="100%" maxW="440px" flex={1} pt={6}>
        <VStack spacing={2} align="stretch">
          <DisplayHeading size="32px">Enter the 6-digit code sent to your phone</DisplayHeading>
        </VStack>
        <FormControl id="verificationCode" flex={1} display="flex" flexDirection="column">
          <Box display="flex" justifyContent="center" mx="-4px">
            <ReactCodeInput
              type="text"
              fields={6}
              value={verificationCode}
              onChange={(value: string) => setVerificationCode(value)}
              inputMode="tel"
              name={''}
              inputStyle={codeInputStyle}
            />
          </Box>
          <Box flex={1} minH="40px" />
          <Button variant="lime" width="full" onClick={handleVerificationCodeSubmit} isLoading={isLoading}>
            Continue
          </Button>
        </FormControl>
        <Text fontSize="15px" color={ff.text2} textAlign="center">
          Don't have an account?{' '}
          <Link to="/register" style={{ color: ff.ink, fontWeight: 600, textDecoration: 'underline' }}>Register now</Link>
        </Text>
      </VStack>
    </Box>
  );
};

export default EnterVerificationCode;
